import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { ArrowLeft, Search, Users, AlertCircle, Link as LinkIcon, ExternalLink, ChevronDown, ChevronRight, Download, FileSpreadsheet, FileText, PieChart, BarChart, DollarSign, Check, Pencil, Scissors, GitMerge, History, Trash2, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import * as Papa from 'papaparse';
import { saveAs } from 'file-saver';
import {
  getFamilies,
  upsertFamilyPayment,
  getFamilyPaymentsForYear,
  getFamilyPayments,
  updateFamily,
  deleteFamily,
  splitFamily,
  mergeFamilies,
} from '../../services/familyService';
import { getActiveSportsYear } from '../../services/sportsYearService';
import { FamilyPaymentStatus, FamilyPayment } from '../../types/models';

interface Participant {
  student_id: string;
  first_name: string;
  last_name: string;
  first_name_tamil?: string;
  last_name_tamil?: string;
  date_of_birth: string;
  gender: string;
  department_id?: string;
  department?: {
    name: string;
    name_tamil?: string;
  };
  house?: {
    house_name: string;
    house_color: string;
  };
  payment_status?: string;
  payment_date?: string;
  payment_notes?: string;
}

interface FamilyGroup {
  familyId?: string;
  lastName: string;
  departmentId: string;
  departmentName: string;
  members: Participant[];
  expanded: boolean;
  yearPaymentStatus?: FamilyPaymentStatus;
  fatherName?: string | null;
  motherName?: string | null;
  guardianName?: string | null;
}

interface Department {
  department_id: string;
  name: string;
  name_tamil?: string;
}

interface Statistics {
  totalFamilies: number;
  totalMembers: number;
  departmentStats: {
    [key: string]: {
      families: number;
      members: number;
    };
  };
}

const FamilyConnectionsPage: React.FC = () => {
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [familyGroups, setFamilyGroups] = useState<FamilyGroup[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [selectedFamilyGroup, setSelectedFamilyGroup] = useState<FamilyGroup | null>(null);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<string>('All');
  const [statistics, setStatistics] = useState<Statistics>({
    totalFamilies: 0,
    totalMembers: 0,
    departmentStats: {}
  });
  const [showStatistics, setShowStatistics] = useState(true);
  const [updatingPaymentStatus, setUpdatingPaymentStatus] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedFamily, setSelectedFamily] = useState<FamilyGroup | null>(null);
  const [paymentFormData, setPaymentFormData] = useState({
    status: 'not_paid',
    date: new Date().toISOString().split('T')[0], // Today's date as default
    notes: ''
  });
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [activeYear, setActiveYear] = useState<number | null>(null);

  // Family edit / create modal (shared form). When editingFamily is null but
  // creatingFamily is true, the form acts as a creation form.
  const [editingFamily, setEditingFamily] = useState<FamilyGroup | null>(null);
  const [creatingFamily, setCreatingFamily] = useState(false);
  const [editFormData, setEditFormData] = useState({
    family_name: '',
    father_name: '',
    mother_name: '',
    guardian_name: '',
    guardian_relationship: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    notes: '',
    department_id: '',
  });
  const [editSaving, setEditSaving] = useState(false);

  // Student picker for the create/edit modal.
  //   editMembers   – student ids currently attached to the family being edited
  //                   (also the staging set for a new family until we save it)
  //   originalMembers – snapshot of the family's members on modal open so we
  //                     know which ids to clear on save
  //   memberSearch  – query string for finding students to add
  //   allStudents   – every student in the system, used as the picker source
  const [editMembers, setEditMembers] = useState<Set<string>>(new Set());
  const [originalMembers, setOriginalMembers] = useState<Set<string>>(new Set());
  const [memberSearch, setMemberSearch] = useState('');
  const [allStudents, setAllStudents] = useState<Array<{
    student_id: string;
    first_name: string;
    last_name: string;
    family_id: string | null;
    family_name?: string | null;
    department_name?: string | null;
    reference_number?: string | null;
  }>>([]);
  // Which name field's autocomplete dropdown is currently open
  const [openNameField, setOpenNameField] = useState<null | 'father' | 'mother' | 'guardian'>(null);

  // Split family modal
  const [splittingFamily, setSplittingFamily] = useState<FamilyGroup | null>(null);
  const [splitSelection, setSplitSelection] = useState<Set<string>>(new Set());
  const [splitNewName, setSplitNewName] = useState('');
  const [splitSaving, setSplitSaving] = useState(false);

  // Merge modal (search-based picker)
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeSourceId, setMergeSourceId] = useState<string>('');
  const [mergeTargetId, setMergeTargetId] = useState<string>('');
  const [mergeSearchSource, setMergeSearchSource] = useState('');
  const [mergeSearchTarget, setMergeSearchTarget] = useState('');
  const [mergeSaving, setMergeSaving] = useState(false);

  // Payment history modal
  const [historyFamily, setHistoryFamily] = useState<FamilyGroup | null>(null);
  const [historyPayments, setHistoryPayments] = useState<FamilyPayment[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    checkAuthorization();
  }, []);

  const checkAuthorization = async () => {
    try {
      const { data: userRoles } = await supabase
        .from('user_roles')
        .select(`
          roles!inner(
            role_name
          )
        `)
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

      const hasAccess = userRoles?.some(ur => 
        ur.roles.role_name === 'admin' || 
        ur.roles.role_name === 'manager' ||
        ur.roles.role_name === 'registrering'
      ) ?? false;

      setIsAuthorized(hasAccess);
      if (!hasAccess) {
        navigate('/');
      } else {
        await Promise.all([
          loadParticipants(),
          loadDepartments()
        ]);
      }
    } catch (error) {
      console.error('Error checking authorization:', error);
      setError('Failed to verify access permissions');
    }
  };

  const loadDepartments = async () => {
    try {
      const { data, error } = await supabase
        .from('departments')
        .select('department_id, name, name_tamil')
        .order('name');
      
      if (error) throw error;
      
      setDepartments(data || []);
    } catch (error: any) {
      console.error('Error loading departments:', error);
      setError(error.message || 'Failed to load departments');
    }
  };

  const loadParticipants = async () => {
    try {
      setLoading(true);
      setError(null);

      const [families, year, studentRoster] = await Promise.all([
        getFamilies(),
        getActiveSportsYear().catch(() => null),
        supabase
          .from('students')
          .select(`
            student_id,
            first_name,
            last_name,
            family_id,
            reference_number,
            family:families(family_name),
            department:departments(name)
          `)
          .order('last_name'),
      ]);
      setActiveYear(year);
      setAllStudents(
        ((studentRoster.data as any[]) || []).map((s) => ({
          student_id: s.student_id,
          first_name: s.first_name,
          last_name: s.last_name,
          family_id: s.family_id,
          family_name: s.family?.family_name || null,
          department_name: s.department?.name || null,
          reference_number: s.reference_number || null,
        }))
      );

      // Load per-year family payment status for the active year, if any
      const paymentsByFamily = new Map<string, FamilyPaymentStatus>();
      if (year) {
        try {
          const payments = await getFamilyPaymentsForYear(year);
          payments.forEach((p) => paymentsByFamily.set(p.family_id, p.status));
        } catch (err) {
          console.warn('Could not load family payments:', err);
        }
      }

      // Pull all students to enrich members with house/department details
      // (the families.members embed has the basics, but house info needs a second hop).
      const { data: studentDetails, error: studentError } = await supabase
        .from('students')
        .select(`
          student_id,
          payment_status,
          payment_date,
          payment_notes,
          department:departments(name, name_tamil),
          house:houses(house_name, house_color)
        `);
      if (studentError) throw studentError;

      const detailsById = new Map<string, any>();
      (studentDetails || []).forEach((s: any) => detailsById.set(s.student_id, s));

      const flatParticipants: Participant[] = [];
      const groups: FamilyGroup[] = families
        .filter((f) => (f.members?.length ?? 0) > 0)
        .map((f) => {
          const members: Participant[] = (f.members || []).map((m: any) => {
            const extra = detailsById.get(m.student_id) || {};
            const enriched: Participant = {
              ...m,
              payment_status: extra.payment_status ?? m.payment_status,
              payment_date: extra.payment_date,
              payment_notes: extra.payment_notes,
              department: extra.department || f.department,
              house: extra.house,
            };
            flatParticipants.push(enriched);
            return enriched;
          });

          return {
            familyId: f.family_id,
            lastName: f.family_name,
            departmentId: f.department_id || '',
            departmentName: f.department?.name || '',
            members,
            expanded: false,
            yearPaymentStatus: paymentsByFamily.get(f.family_id),
            fatherName: f.father_name,
            motherName: f.mother_name,
            guardianName: f.guardian_name,
          };
        })
        .sort((a, b) => {
          const memberDiff = b.members.length - a.members.length;
          if (memberDiff !== 0) return memberDiff;
          return a.lastName.localeCompare(b.lastName);
        });

      setParticipants(flatParticipants);
      setFamilyGroups(groups);
      calculateStatistics(groups);
    } catch (error: any) {
      console.error('Error loading families:', error);
      setError(error.message || 'Failed to load families');
    } finally {
      setLoading(false);
    }
  };

  const calculateStatistics = (groups: FamilyGroup[]) => {
    if (groups.length === 0) {
      setStatistics({
        totalFamilies: 0,
        totalMembers: 0,
        departmentStats: {}
      });
      return;
    }

    // Calculate total families and members
    const totalFamilies = groups.length;
    const totalMembers = groups.reduce((sum, group) => sum + group.members.length, 0);

    // Calculate department statistics
    const departmentStats: {
      [key: string]: {
        families: number;
        members: number;
      };
    } = {};

    groups.forEach(group => {
      if (!departmentStats[group.departmentId]) {
        departmentStats[group.departmentId] = {
          families: 0,
          members: 0
        };
      }
      departmentStats[group.departmentId].families += 1;
      departmentStats[group.departmentId].members += group.members.length;
    });

    setStatistics({
      totalFamilies,
      totalMembers,
      departmentStats
    });
  };

  const toggleFamilyExpanded = (index: number) => {
    setFamilyGroups(prevGroups => {
      const newGroups = [...prevGroups];
      newGroups[index] = {
        ...newGroups[index],
        expanded: !newGroups[index].expanded
      };
      return newGroups;
    });
  };

  const getFilteredFamilyGroups = () => {
    return familyGroups.filter(group => {
      // Apply department filter
      const matchesDepartment = selectedDepartment === 'All' || group.departmentId === selectedDepartment;
      
      // Apply search filter
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = (
        group.lastName.toLowerCase().includes(searchLower) ||
        group.departmentName.toLowerCase().includes(searchLower) ||
        group.members.some(member => 
          `${member.first_name} ${member.last_name}`.toLowerCase().includes(searchLower) ||
          (member.first_name_tamil && member.last_name_tamil && 
            `${member.first_name_tamil} ${member.last_name_tamil}`.toLowerCase().includes(searchLower))
        )
      );
      
      return matchesDepartment && matchesSearch;
    });
  };

  const filteredFamilyGroups = getFilteredFamilyGroups();

  useEffect(() => {
    // Recalculate statistics when filters change
    calculateStatistics(filteredFamilyGroups);
  }, [searchTerm, selectedDepartment]);

  // Open the edit modal when navigated here with ?edit=<familyId>.
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId || familyGroups.length === 0) return;
    const group = familyGroups.find((g) => g.familyId === editId);
    if (group) {
      openEditModal(group);
      const next = new URLSearchParams(searchParams);
      next.delete('edit');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, familyGroups]);

  const handleExportToExcel = (familyGroup: FamilyGroup | null) => {
    const dataToExport = familyGroup ? [familyGroup] : filteredFamilyGroups;
    
    // Prepare data for export
    const exportData: any[] = [];
    
    dataToExport.forEach(group => {
      // Add a row for the family header
      exportData.push({
        'Family': group.lastName,
        'Department': group.departmentName,
        'Members': group.members.length,
        'First Name': '',
        'Last Name': '',
        'Tamil First Name': '',
        'Tamil Last Name': '',
        'Gender': '',
        'Date of Birth': '',
        'House': '',
        'Payment Status': '',
        'Payment Date': '',
        'Payment Notes': ''
      });
      
      // Add rows for each family member
      group.members.forEach(member => {
        exportData.push({
          'Family': '',
          'Department': '',
          'Members': '',
          'First Name': member.first_name,
          'Last Name': member.last_name,
          'Tamil First Name': member.first_name_tamil || '',
          'Tamil Last Name': member.last_name_tamil || '',
          'Gender': member.gender === 'M' ? t('participants.filters.male') : t('participants.filters.female'),
          'Date of Birth': new Date(member.date_of_birth).toLocaleDateString(),
          'House': member.house?.house_name || '',
          'Payment Status': member.payment_status || 'not_paid',
          'Payment Date': member.payment_date ? new Date(member.payment_date).toLocaleDateString() : '',
          'Payment Notes': member.payment_notes || ''
        });
      });
      
      // Add an empty row between families
      exportData.push({
        'Family': '',
        'Department': '',
        'Members': '',
        'First Name': '',
        'Last Name': '',
        'Tamil First Name': '',
        'Tamil Last Name': '',
        'Gender': '',
        'Date of Birth': '',
        'House': '',
        'Payment Status': '',
        'Payment Date': '',
        'Payment Notes': ''
      });
    });
    
    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Family Connections");
    
    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `family_connections_${new Date().toISOString().split('T')[0]}.xlsx`);
    
    setShowExportMenu(false);
    setSelectedFamilyGroup(null);
  };

  const handleExportToCSV = (familyGroup: FamilyGroup | null) => {
    const dataToExport = familyGroup ? [familyGroup] : filteredFamilyGroups;
    
    // Prepare data for export
    const exportData: any[] = [];
    
    dataToExport.forEach(group => {
      // Add a row for the family header
      exportData.push({
        'Family': group.lastName,
        'Department': group.departmentName,
        'Members': group.members.length,
        'First Name': '',
        'Last Name': '',
        'Tamil First Name': '',
        'Tamil Last Name': '',
        'Gender': '',
        'Date of Birth': '',
        'House': '',
        'Payment Status': '',
        'Payment Date': '',
        'Payment Notes': ''
      });
      
      // Add rows for each family member
      group.members.forEach(member => {
        exportData.push({
          'Family': '',
          'Department': '',
          'Members': '',
          'First Name': member.first_name,
          'Last Name': member.last_name,
          'Tamil First Name': member.first_name_tamil || '',
          'Tamil Last Name': member.last_name_tamil || '',
          'Gender': member.gender === 'M' ? t('participants.filters.male') : t('participants.filters.female'),
          'Date of Birth': new Date(member.date_of_birth).toLocaleDateString(),
          'House': member.house?.house_name || '',
          'Payment Status': member.payment_status || 'not_paid',
          'Payment Date': member.payment_date ? new Date(member.payment_date).toLocaleDateString() : '',
          'Payment Notes': member.payment_notes || ''
        });
      });
      
      // Add an empty row between families
      exportData.push({
        'Family': '',
        'Department': '',
        'Members': '',
        'First Name': '',
        'Last Name': '',
        'Tamil First Name': '',
        'Tamil Last Name': '',
        'Gender': '',
        'Date of Birth': '',
        'House': '',
        'Payment Status': '',
        'Payment Date': '',
        'Payment Notes': ''
      });
    });
    
    // Generate CSV
    const csv = Papa.unparse(exportData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `family_connections_${new Date().toISOString().split('T')[0]}.csv`);
    
    setShowExportMenu(false);
    setSelectedFamilyGroup(null);
  };

  const openPaymentModal = (family: FamilyGroup) => {
    // Set default values based on the first family member with payment info
    const memberWithPayment = family.members.find(m => m.payment_status);
    
    setPaymentFormData({
      status: memberWithPayment?.payment_status || 'not_paid',
      date: new Date().toISOString().split('T')[0], // Today's date as default
      notes: memberWithPayment?.payment_notes || ''
    });
    
    setSelectedFamily(family);
    setShowPaymentModal(true);
    setUpdateSuccess(false);
  };

  const handlePaymentStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setPaymentFormData({
      ...paymentFormData,
      status: e.target.value
    });
  };

  const handlePaymentDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPaymentFormData({
      ...paymentFormData,
      date: e.target.value
    });
  };

  const handlePaymentNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPaymentFormData({
      ...paymentFormData,
      notes: e.target.value
    });
  };

  const resetEditForm = () =>
    setEditFormData({
      family_name: '',
      father_name: '',
      mother_name: '',
      guardian_name: '',
      guardian_relationship: '',
      contact_email: '',
      contact_phone: '',
      address: '',
      notes: '',
      department_id: '',
    });

  const openCreateModal = () => {
    resetEditForm();
    setEditingFamily(null);
    setCreatingFamily(true);
    setEditMembers(new Set());
    setOriginalMembers(new Set());
    setMemberSearch('');
  };

  const openEditModal = (group: FamilyGroup) => {
    setEditingFamily(group);
    setCreatingFamily(false);
    resetEditForm();
    setEditFormData((prev) => ({
      ...prev,
      family_name: group.lastName,
      department_id: group.departmentId || '',
    }));
    const memberIds = new Set(group.members.map((m) => m.student_id));
    setEditMembers(memberIds);
    setOriginalMembers(new Set(memberIds));
    setMemberSearch('');
    if (group.familyId) {
      supabase
        .from('families')
        .select('father_name, mother_name, guardian_name, guardian_relationship, contact_email, contact_phone, address, notes')
        .eq('family_id', group.familyId)
        .maybeSingle()
        .then(({ data }) => {
          if (data) {
            setEditFormData((prev) => ({
              ...prev,
              father_name: data.father_name || '',
              mother_name: data.mother_name || '',
              guardian_name: data.guardian_name || '',
              guardian_relationship: data.guardian_relationship || '',
              contact_email: data.contact_email || '',
              contact_phone: data.contact_phone || '',
              address: data.address || '',
              notes: data.notes || '',
            }));
          }
        });
    }
  };

  const handleEditSave = async () => {
    if (!editFormData.family_name.trim()) {
      setError('Family name is required');
      return;
    }
    try {
      setEditSaving(true);
      const payload = {
        family_name: editFormData.family_name.trim(),
        father_name: editFormData.father_name || null,
        mother_name: editFormData.mother_name || null,
        guardian_name: editFormData.guardian_name || null,
        guardian_relationship: editFormData.guardian_relationship || null,
        contact_email: editFormData.contact_email || null,
        contact_phone: editFormData.contact_phone || null,
        address: editFormData.address || null,
        notes: editFormData.notes || null,
        department_id: editFormData.department_id || null,
      };
      let familyId = editingFamily?.familyId;
      if (familyId) {
        await updateFamily(familyId, payload);
      } else {
        const created = await (await import('../../services/familyService')).createFamily(payload);
        familyId = created.family_id;
      }

      // Apply membership diff: link newly added, unlink removed.
      const toLink = Array.from(editMembers).filter((id) => !originalMembers.has(id));
      const toUnlink = Array.from(originalMembers).filter((id) => !editMembers.has(id));
      if (toLink.length > 0) {
        const { error: linkErr } = await supabase
          .from('students')
          .update({ family_id: familyId })
          .in('student_id', toLink);
        if (linkErr) throw linkErr;
      }
      if (toUnlink.length > 0) {
        const { error: unlinkErr } = await supabase
          .from('students')
          .update({ family_id: null })
          .in('student_id', toUnlink);
        if (unlinkErr) throw unlinkErr;
      }

      setEditingFamily(null);
      setCreatingFamily(false);
      setEditMembers(new Set());
      setOriginalMembers(new Set());
      await loadParticipants();
    } catch (err: any) {
      setError(err.message || 'Failed to save family');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDeleteFamily = async (group: FamilyGroup) => {
    if (!group.familyId) return;
    if (!window.confirm(
      `Delete the ${group.lastName} family? Members will keep existing but lose the family link.`
    )) return;
    try {
      await deleteFamily(group.familyId);
      await loadParticipants();
    } catch (err: any) {
      setError(err.message || 'Failed to delete family');
    }
  };

  const openSplitModal = (group: FamilyGroup) => {
    setSplittingFamily(group);
    setSplitSelection(new Set());
    setSplitNewName(group.lastName);
  };

  const toggleSplitMember = (studentId: string) => {
    setSplitSelection((prev) => {
      const next = new Set(prev);
      if (next.has(studentId)) next.delete(studentId);
      else next.add(studentId);
      return next;
    });
  };

  const handleSplitSave = async () => {
    if (!splittingFamily?.familyId) return;
    if (splitSelection.size === 0) {
      setError('Select at least one member to move into a new family');
      return;
    }
    try {
      setSplitSaving(true);
      await splitFamily(
        {
          family_id: splittingFamily.familyId,
          family_name: splittingFamily.lastName,
          department_id: splittingFamily.departmentId || null,
        } as any,
        Array.from(splitSelection),
        splitNewName
      );
      setSplittingFamily(null);
      setSplitSelection(new Set());
      await loadParticipants();
    } catch (err: any) {
      setError(err.message || 'Failed to split family');
    } finally {
      setSplitSaving(false);
    }
  };

  const handleMergeSave = async () => {
    if (!mergeSourceId || !mergeTargetId) {
      setError('Pick both source and target families');
      return;
    }
    if (mergeSourceId === mergeTargetId) {
      setError('Source and target must differ');
      return;
    }
    try {
      setMergeSaving(true);
      await mergeFamilies(mergeSourceId, mergeTargetId);
      setMergeOpen(false);
      setMergeSourceId('');
      setMergeTargetId('');
      await loadParticipants();
    } catch (err: any) {
      setError(err.message || 'Failed to merge families');
    } finally {
      setMergeSaving(false);
    }
  };

  const openHistoryModal = async (group: FamilyGroup) => {
    if (!group.familyId) return;
    setHistoryFamily(group);
    setHistoryLoading(true);
    try {
      const rows = await getFamilyPayments(group.familyId);
      setHistoryPayments(rows);
    } catch (err: any) {
      setError(err.message || 'Failed to load payment history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const updateFamilyPaymentStatus = async () => {
    if (!selectedFamily) return;

    try {
      setUpdatingPaymentStatus(true);
      setError(null);

      // Get all student IDs in the family
      const studentIds = selectedFamily.members.map(member => member.student_id);

      // Prepare the update data
      const updateData = {
        payment_status: paymentFormData.status,
        payment_date: paymentFormData.date || null,
        payment_notes: paymentFormData.notes
      };

      // Update all family members (legacy per-student status, still used in some views)
      const { error: updateError } = await supabase
        .from('students')
        .update(updateData)
        .in('student_id', studentIds);

      if (updateError) throw updateError;

      // Also record the payment at the family level for the active sports year
      if (selectedFamily.familyId && activeYear) {
        try {
          await upsertFamilyPayment({
            family_id: selectedFamily.familyId,
            competition_year: activeYear,
            status: paymentFormData.status as FamilyPaymentStatus,
            paid_at: paymentFormData.date || null,
            notes: paymentFormData.notes || null,
          });
        } catch (err: any) {
          console.warn('Could not write family_payments:', err.message);
        }
      }

      // Update local state
      setFamilyGroups(prevGroups => {
        return prevGroups.map(group => {
          if (group.familyId === selectedFamily.familyId) {
            return {
              ...group,
              yearPaymentStatus: paymentFormData.status as FamilyPaymentStatus,
              members: group.members.map(member => ({
                ...member,
                payment_status: paymentFormData.status,
                payment_date: paymentFormData.date,
                payment_notes: paymentFormData.notes
              }))
            };
          }
          return group;
        });
      });

      setUpdateSuccess(true);

      // Close modal after a delay
      setTimeout(() => {
        setShowPaymentModal(false);
        setSelectedFamily(null);
      }, 1500);
      
    } catch (error: any) {
      console.error('Error updating payment status:', error);
      setError(error.message || 'Failed to update payment status');
    } finally {
      setUpdatingPaymentStatus(false);
    }
  };

  if (!isAuthorized) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="mr-4 inline-flex items-center p-2 border border-transparent rounded-full shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <ArrowLeft className="h-5 w-5" aria-hidden="true" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('familyConnections.title')}
          </h1>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowStatistics(!showStatistics)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <BarChart className="h-4 w-4 mr-2" />
            {showStatistics ? t('medalRequirements.hideStatistics') : t('medalRequirements.showStatistics')}
          </button>
          <button
            onClick={openCreateModal}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Users className="h-4 w-4 mr-2" />
            Create family
          </button>
          <button
            onClick={() => setMergeOpen(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <GitMerge className="h-4 w-4 mr-2" />
            Merge families
          </button>
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Download className="h-4 w-4 mr-2" />
              {t('familyConnections.export.title')}
            </button>
            
            {showExportMenu && (
              <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                <div className="py-1" role="menu">
                  <button
                    onClick={() => handleExportToExcel(selectedFamilyGroup)}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    {t('familyConnections.export.toExcel')}
                  </button>
                  <button
                    onClick={() => handleExportToCSV(selectedFamilyGroup)}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    {t('familyConnections.export.toCSV')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Statistics Section */}
      {showStatistics && (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 bg-blue-50">
            <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
              <PieChart className="h-5 w-5 text-blue-500 mr-2" />
              {t('familyConnections.title')}
            </h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <div className="text-sm font-medium text-gray-500">{t('familyConnections.family')}</div>
                <div className="mt-1 text-3xl font-semibold text-gray-900">{statistics.totalFamilies}</div>
              </div>
              
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                <div className="text-sm font-medium text-gray-500">{t('familyConnections.members')}</div>
                <div className="mt-1 text-3xl font-semibold text-gray-900">{statistics.totalMembers}</div>
              </div>
            </div>

            {/* Department Statistics */}
            {Object.keys(statistics.departmentStats).length > 0 && (
              <div className="mt-6">
                <h4 className="text-lg font-medium text-gray-900 mb-4">{t('departments.title')}</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('departments.table.name')}
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('familyConnections.family')}
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('familyConnections.members')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {Object.entries(statistics.departmentStats).map(([deptId, stats]) => {
                        const department = departments.find(d => d.department_id === deptId);
                        return (
                          <tr key={deptId}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {department?.name || 'Unknown'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {stats.families}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {stats.members}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
          <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-4 md:items-center">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('familyConnections.searchPlaceholder')}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div className="flex-shrink-0">
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="All">{t('participants.filters.allDepartments')}</option>
                {departments.map(dept => (
                  <option key={dept.department_id} value={dept.department_id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : filteredFamilyGroups.length === 0 ? (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {t('familyConnections.noConnections.title')}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || selectedDepartment !== 'All' ? 
                t('familyConnections.noConnections.description.withFilters') : 
                t('familyConnections.noConnections.description.noFilters')}
            </p>
          </div>
        ) : (
          <div className="space-y-4 p-6">
            {filteredFamilyGroups.map((group, index) => (
              <div key={`${group.lastName}_${group.departmentId}_${index}`} className="bg-white shadow overflow-hidden sm:rounded-lg border border-gray-200">
                <div 
                  className="px-4 py-5 sm:px-6 bg-gray-50 cursor-pointer"
                  onClick={() => {
                    toggleFamilyExpanded(index);
                    setSelectedFamilyGroup(group);
                  }}
                >
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      {group.expanded ? 
                        <ChevronDown className="h-5 w-5 text-blue-500 mr-2" /> : 
                        <ChevronRight className="h-5 w-5 text-blue-500 mr-2" />
                      }
                      <div>
                        <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
                          <LinkIcon className="h-5 w-5 text-blue-500 mr-2" />
                          {group.familyId ? (
                            <Link
                              to={`/participants/family/${group.familyId}`}
                              onClick={(e) => e.stopPropagation()}
                              className="hover:text-blue-700 hover:underline"
                            >
                              {group.lastName} {t('familyConnections.family')}
                            </Link>
                          ) : (
                            <>{group.lastName} {t('familyConnections.family')}</>
                          )}
                        </h3>
                        <p className="mt-1 max-w-2xl text-sm text-gray-500">
                          {group.departmentName} • {group.members.length} {t('familyConnections.members')}
                        </p>
                        {(group.fatherName || group.motherName || group.guardianName) && (
                          <p className="mt-1 max-w-2xl text-xs text-gray-600">
                            {group.fatherName && <span className="mr-2">Father: <span className="font-medium">{group.fatherName}</span></span>}
                            {group.motherName && <span className="mr-2">Mother: <span className="font-medium">{group.motherName}</span></span>}
                            {group.guardianName && <span>Guardian: <span className="font-medium">{group.guardianName}</span></span>}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      {group.yearPaymentStatus && (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          group.yearPaymentStatus === 'paid' ? 'bg-green-100 text-green-800' :
                          group.yearPaymentStatus === 'school_paid' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {activeYear} · {group.yearPaymentStatus}
                        </span>
                      )}
                      <button
                        onClick={() => openPaymentModal(group)}
                        className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                        title="Record payment"
                      >
                        <DollarSign className="h-3 w-3 mr-1" />
                        Payment
                      </button>
                      <button
                        onClick={() => openHistoryModal(group)}
                        className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                        title="Payment history per year"
                      >
                        <History className="h-3 w-3 mr-1" />
                        History
                      </button>
                      <button
                        onClick={() => openEditModal(group)}
                        className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                        title="Edit family"
                      >
                        <Pencil className="h-3 w-3 mr-1" />
                        Edit
                      </button>
                      <button
                        onClick={() => openSplitModal(group)}
                        disabled={group.members.length < 2}
                        className="inline-flex items-center px-2 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                        title="Move some members to a new family"
                      >
                        <Scissors className="h-3 w-3 mr-1" />
                        Split
                      </button>
                      <button
                        onClick={() => handleDeleteFamily(group)}
                        className="inline-flex items-center px-2 py-1 border border-red-200 shadow-sm text-xs font-medium rounded text-red-700 bg-white hover:bg-red-50"
                        title="Delete this family (students stay, link is removed)"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                </div>
                
                {group.expanded && (
                  <div className="border-t border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('familyConnections.table.name')}
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('familyConnections.table.tamilName')}
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('familyConnections.table.gender')}
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('familyConnections.table.dateOfBirth')}
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('familyConnections.table.house')}
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('participants.form.fields.paymentStatus.label')}
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {t('familyConnections.table.actions')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {group.members.map((member) => (
                          <tr key={member.student_id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {member.first_name} {member.last_name}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {member.first_name_tamil} {member.last_name_tamil}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                member.gender === 'M' ? 'bg-blue-100 text-blue-800' : 'bg-pink-100 text-pink-800'
                              }`}>
                                {member.gender === 'M' ? t('participants.filters.male') : t('participants.filters.female')}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {new Date(member.date_of_birth).toLocaleDateString()}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                {member.house && (
                                  <>
                                    <div 
                                      className="h-3 w-3 rounded-full mr-2" 
                                      style={{ backgroundColor: member.house.house_color }}
                                    />
                                    <div className="text-sm text-gray-900">
                                      {member.house.house_name}
                                    </div>
                                  </>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                member.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                                member.payment_status === 'school_paid' ? 'bg-blue-100 text-blue-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {member.payment_status === 'paid' ? t('reports.payments.status.paid') :
                                 member.payment_status === 'school_paid' ? t('reports.payments.status.school_paid') :
                                 t('reports.payments.status.not_paid')}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <Link
                                to={`/participants/${member.student_id}`}
                                className="text-blue-600 hover:text-blue-900 inline-flex items-center"
                              >
                                <ExternalLink className="h-4 w-4 mr-1" />
                                {t('familyConnections.view')}
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Payment Status Modal */}
      {showPaymentModal && selectedFamily && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                {t('participants.form.fields.paymentStatus.label')} - {selectedFamily.lastName} {t('familyConnections.family')}
              </h2>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {updateSuccess && (
              <div className="mb-4 bg-green-50 border-l-4 border-green-500 p-4">
                <div className="flex">
                  <Check className="h-5 w-5 text-green-400" />
                  <div className="ml-3">
                    <p className="text-sm text-green-700">{t('registrationSettings.moduleToggleSuccess')}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label htmlFor="payment_status" className="block text-sm font-medium text-gray-700">
                  {t('participants.form.fields.paymentStatus.label')}
                </label>
                <select
                  id="payment_status"
                  value={paymentFormData.status}
                  onChange={handlePaymentStatusChange}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                >
                  <option value="not_paid">{t('participants.form.fields.paymentStatus.options.notPaid')}</option>
                  <option value="paid">{t('participants.form.fields.paymentStatus.options.paid')}</option>
                  <option value="school_paid">{t('participants.form.fields.paymentStatus.options.schoolPaid')}</option>
                </select>
              </div>

              <div>
                <label htmlFor="payment_date" className="block text-sm font-medium text-gray-700">
                  {t('participants.form.fields.paymentDate.label')}
                </label>
                <input
                  type="date"
                  id="payment_date"
                  value={paymentFormData.date}
                  onChange={handlePaymentDateChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="payment_notes" className="block text-sm font-medium text-gray-700">
                  {t('participants.form.fields.paymentNotes.label')}
                </label>
                <textarea
                  id="payment_notes"
                  rows={3}
                  value={paymentFormData.notes}
                  onChange={handlePaymentNotesChange}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder={t('participants.form.fields.paymentNotes.placeholder')}
                />
              </div>

              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="button"
                  onClick={updateFamilyPaymentStatus}
                  disabled={updatingPaymentStatus}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:col-start-2 sm:text-sm"
                >
                  {updatingPaymentStatus ? t('competitions.actions.updating') : t('competitions.actions.save')}
                </button>
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                >
                  {t('competitions.actions.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Family create / edit modal */}
      {(editingFamily || creatingFamily) && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">
                {editingFamily ? `Edit family · ${editingFamily.lastName}` : 'Create family'}
              </h3>
              <button
                onClick={() => { setEditingFamily(null); setCreatingFamily(false); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Family name *</label>
                  <input
                    type="text"
                    value={editFormData.family_name}
                    onChange={(e) => setEditFormData({ ...editFormData, family_name: e.target.value })}
                    placeholder="Display name for this family"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Department</label>
                  <select
                    value={editFormData.department_id}
                    onChange={(e) => setEditFormData({ ...editFormData, department_id: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">—</option>
                    {departments.map((d) => (
                      <option key={d.department_id} value={d.department_id}>{d.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Parents / Guardians</h4>
                {(() => {
                  // Collect unique parent names already in the system so the
                  // admin can re-use exact spellings instead of re-typing.
                  const existing = {
                    father: new Set<string>(),
                    mother: new Set<string>(),
                    guardian: new Set<string>(),
                  };
                  familyGroups.forEach((g) => {
                    if (g.fatherName) existing.father.add(g.fatherName);
                    if (g.motherName) existing.mother.add(g.motherName);
                    if (g.guardianName) existing.guardian.add(g.guardianName);
                  });

                  const suggestionsFor = (
                    kind: 'father' | 'mother' | 'guardian',
                    query: string,
                  ) => {
                    const needle = query.trim().toLowerCase();
                    if (!needle) return [] as Array<{ label: string; sub?: string }>;
                    const out: Array<{ label: string; sub?: string }> = [];
                    Array.from(existing[kind])
                      .filter((n) => n.toLowerCase().includes(needle))
                      .slice(0, 8)
                      .forEach((n) => out.push({ label: n, sub: `existing ${kind}` }));
                    allStudents
                      .filter((s) =>
                        `${s.first_name} ${s.last_name}`.toLowerCase().includes(needle),
                      )
                      .slice(0, 8)
                      .forEach((s) =>
                        out.push({
                          label: `${s.first_name} ${s.last_name}`,
                          sub: `participant${s.family_name ? ` · in ${s.family_name}` : ''}`,
                        }),
                      );
                    const seen = new Set<string>();
                    return out.filter((item) => {
                      if (seen.has(item.label)) return false;
                      seen.add(item.label);
                      return true;
                    });
                  };

                  const NameField = ({
                    kind,
                    label,
                    placeholder,
                    fieldKey,
                  }: {
                    kind: 'father' | 'mother' | 'guardian';
                    label: string;
                    placeholder?: string;
                    fieldKey: 'father_name' | 'mother_name' | 'guardian_name';
                  }) => {
                    const value = editFormData[fieldKey];
                    const suggestions = suggestionsFor(kind, value);
                    const isOpen = openNameField === kind && suggestions.length > 0;
                    return (
                      <div className="relative">
                        <label className="block text-xs font-medium text-gray-600">{label}</label>
                        <input
                          type="text"
                          value={value}
                          placeholder={placeholder}
                          onFocus={() => setOpenNameField(kind)}
                          onBlur={() => setTimeout(() => setOpenNameField((cur) => (cur === kind ? null : cur)), 150)}
                          onChange={(e) => {
                            setEditFormData({ ...editFormData, [fieldKey]: e.target.value });
                            setOpenNameField(kind);
                          }}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500"
                        />
                        {isOpen && (
                          <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-56 overflow-y-auto">
                            {suggestions.map((s, i) => (
                              <button
                                key={`${s.label}-${i}`}
                                type="button"
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  setEditFormData({ ...editFormData, [fieldKey]: s.label });
                                  setOpenNameField(null);
                                }}
                                className="w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 border-b last:border-b-0"
                              >
                                <span className="font-medium text-gray-900">{s.label}</span>
                                {s.sub && (
                                  <span className="text-xs text-gray-500 ml-2">{s.sub}</span>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  };

                  return (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <NameField kind="father" label="Father name" fieldKey="father_name" />
                      <NameField kind="mother" label="Mother name" fieldKey="mother_name" />
                      <NameField
                        kind="guardian"
                        label="Guardian name (if not parent)"
                        fieldKey="guardian_name"
                      />
                      <div>
                        <label className="block text-xs font-medium text-gray-600">
                          Guardian relationship
                        </label>
                        <input
                          type="text"
                          value={editFormData.guardian_relationship}
                          onChange={(e) =>
                            setEditFormData({
                              ...editFormData,
                              guardian_relationship: e.target.value,
                            })
                          }
                          placeholder="e.g. uncle, grandmother"
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Contact</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600">Email</label>
                    <input
                      type="email"
                      value={editFormData.contact_email}
                      onChange={(e) => setEditFormData({ ...editFormData, contact_email: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600">Phone</label>
                    <input
                      type="tel"
                      value={editFormData.contact_phone}
                      onChange={(e) => setEditFormData({ ...editFormData, contact_phone: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-medium text-gray-600">Address</label>
                    <input
                      type="text"
                      value={editFormData.address}
                      onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Notes</label>
                <textarea
                  rows={3}
                  value={editFormData.notes}
                  onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {(() => {
                const linkedStudents = allStudents.filter((s) => editMembers.has(s.student_id));
                const needle = memberSearch.trim().toLowerCase();
                const candidates = !needle
                  ? []
                  : allStudents
                      .filter((s) => !editMembers.has(s.student_id))
                      .filter((s) => {
                        const haystack = [
                          `${s.first_name} ${s.last_name}`,
                          s.family_name || '',
                          s.department_name || '',
                          s.reference_number || '',
                        ]
                          .join(' ')
                          .toLowerCase();
                        return haystack.includes(needle);
                      })
                      .slice(0, 25);

                return (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">
                      Members ({linkedStudents.length})
                    </h4>
                    {linkedStudents.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {linkedStudents.map((s) => (
                          <span
                            key={s.student_id}
                            className="inline-flex items-center bg-blue-100 text-blue-800 rounded-full px-3 py-1 text-xs"
                          >
                            {s.first_name} {s.last_name}
                            <button
                              type="button"
                              onClick={() => {
                                const next = new Set(editMembers);
                                next.delete(s.student_id);
                                setEditMembers(next);
                              }}
                              className="ml-2 text-blue-600 hover:text-blue-900"
                              aria-label="Remove"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    <input
                      type="text"
                      value={memberSearch}
                      onChange={(e) => setMemberSearch(e.target.value)}
                      placeholder="Search students by name, reference, department or current family"
                      className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                    {needle && (
                      <div className="mt-1 border border-gray-200 rounded-md max-h-56 overflow-y-auto bg-white">
                        {candidates.length === 0 && (
                          <div className="p-2 text-sm text-gray-500">No matches</div>
                        )}
                        {candidates.map((s) => (
                          <button
                            key={s.student_id}
                            type="button"
                            onClick={() => {
                              const next = new Set(editMembers);
                              next.add(s.student_id);
                              setEditMembers(next);
                              setMemberSearch('');
                            }}
                            className="w-full text-left px-3 py-1.5 text-sm hover:bg-blue-50 border-b last:border-b-0"
                          >
                            <span className="font-medium text-gray-900">
                              {s.first_name} {s.last_name}
                            </span>
                            <span className="text-xs text-gray-500 ml-2">
                              {s.department_name && <>{s.department_name} · </>}
                              {s.family_name ? (
                                <span className="text-amber-600">already in: {s.family_name}</span>
                              ) : (
                                <span className="text-green-600">unassigned</span>
                              )}
                              {s.reference_number && <> · #{s.reference_number}</>}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Adding a student already in another family will move them on save.
                    </p>
                  </div>
                );
              })()}
            </div>
            <div className="px-6 py-4 border-t flex justify-end gap-2">
              <button
                onClick={() => { setEditingFamily(null); setCreatingFamily(false); }}
                className="px-4 py-2 border border-gray-300 rounded text-sm text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleEditSave}
                disabled={editSaving}
                className="px-4 py-2 border border-transparent rounded text-sm text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300"
              >
                {editSaving ? 'Saving...' : editingFamily ? 'Save changes' : 'Create family'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Split family modal */}
      {splittingFamily && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Split {splittingFamily.lastName} family</h3>
              <button onClick={() => setSplittingFamily(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-3">
              <p className="text-sm text-gray-500">
                Select members to move into a new family. Unselected members stay in the original.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700">New family name</label>
                <input
                  type="text"
                  value={splitNewName}
                  onChange={(e) => setSplitNewName(e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="border rounded divide-y max-h-60 overflow-y-auto">
                {splittingFamily.members.map((m) => (
                  <label key={m.student_id} className="flex items-center px-3 py-2 hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={splitSelection.has(m.student_id)}
                      onChange={() => toggleSplitMember(m.student_id)}
                      className="mr-3 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-900">
                      {m.first_name} {m.last_name}
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <div className="px-6 py-4 border-t flex justify-end gap-2">
              <button
                onClick={() => setSplittingFamily(null)}
                className="px-4 py-2 border border-gray-300 rounded text-sm text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSplitSave}
                disabled={splitSaving || splitSelection.size === 0}
                className="px-4 py-2 border border-transparent rounded text-sm text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300"
              >
                {splitSaving ? 'Splitting...' : `Move ${splitSelection.size} to new family`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Merge families modal (search-based picker) */}
      {mergeOpen && (() => {
        const searchableGroups = familyGroups.filter((g) => g.familyId);

        const matches = (g: FamilyGroup, q: string) => {
          if (!q.trim()) return true;
          const needle = q.toLowerCase();
          const memberNames = g.members
            .map((m) => `${m.first_name} ${m.last_name}`.toLowerCase())
            .join(' ');
          return (
            g.lastName.toLowerCase().includes(needle) ||
            (g.departmentName || '').toLowerCase().includes(needle) ||
            memberNames.includes(needle)
          );
        };

        const sourceMatches = searchableGroups.filter((g) => matches(g, mergeSearchSource));
        const targetMatches = searchableGroups
          .filter((g) => g.familyId !== mergeSourceId)
          .filter((g) => matches(g, mergeSearchTarget));
        const selectedSource = searchableGroups.find((g) => g.familyId === mergeSourceId);
        const selectedTarget = searchableGroups.find((g) => g.familyId === mergeTargetId);

        const renderRow = (g: FamilyGroup, isSelected: boolean, onPick: () => void) => (
          <button
            key={g.familyId}
            type="button"
            onClick={onPick}
            className={`w-full text-left px-3 py-2 text-sm border-b last:border-b-0 hover:bg-blue-50 ${
              isSelected ? 'bg-blue-100' : 'bg-white'
            }`}
          >
            <div className="font-medium text-gray-900">
              {g.lastName} <span className="text-gray-500 font-normal">· {g.members.length} members</span>
            </div>
            {g.departmentName && (
              <div className="text-xs text-gray-500">{g.departmentName}</div>
            )}
            <div className="text-xs text-gray-600 truncate">
              {g.members.slice(0, 4).map((m) => `${m.first_name} ${m.last_name}`).join(', ')}
              {g.members.length > 4 ? '…' : ''}
            </div>
          </button>
        );

        return (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
              <div className="px-6 py-4 border-b flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Merge families</h3>
                <button
                  onClick={() => {
                    setMergeOpen(false);
                    setMergeSourceId('');
                    setMergeTargetId('');
                    setMergeSearchSource('');
                    setMergeSearchTarget('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="px-6 py-4 overflow-y-auto flex-1">
                <p className="text-sm text-gray-500 mb-4">
                  All members of the source family will be moved into the target family, and the
                  source is deleted. Search by family name, department or student name.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="border rounded-lg flex flex-col min-h-0">
                    <div className="px-3 py-2 border-b bg-red-50">
                      <div className="text-xs font-semibold text-red-700 uppercase tracking-wide">
                        Source · will be deleted
                      </div>
                      <input
                        type="text"
                        value={mergeSearchSource}
                        onChange={(e) => setMergeSearchSource(e.target.value)}
                        placeholder="Search..."
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div className="overflow-y-auto max-h-80">
                      {sourceMatches.length === 0 && (
                        <div className="p-3 text-sm text-gray-500">No matches</div>
                      )}
                      {sourceMatches.map((g) =>
                        renderRow(g, g.familyId === mergeSourceId, () => setMergeSourceId(g.familyId!))
                      )}
                    </div>
                    {selectedSource && (
                      <div className="px-3 py-2 border-t bg-red-50 text-xs">
                        <span className="font-semibold">Selected:</span> {selectedSource.lastName} ({selectedSource.members.length})
                      </div>
                    )}
                  </div>
                  <div className="border rounded-lg flex flex-col min-h-0">
                    <div className="px-3 py-2 border-b bg-green-50">
                      <div className="text-xs font-semibold text-green-700 uppercase tracking-wide">
                        Target · kept and grown
                      </div>
                      <input
                        type="text"
                        value={mergeSearchTarget}
                        onChange={(e) => setMergeSearchTarget(e.target.value)}
                        placeholder="Search..."
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-1.5 px-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div className="overflow-y-auto max-h-80">
                      {targetMatches.length === 0 && (
                        <div className="p-3 text-sm text-gray-500">No matches</div>
                      )}
                      {targetMatches.map((g) =>
                        renderRow(g, g.familyId === mergeTargetId, () => setMergeTargetId(g.familyId!))
                      )}
                    </div>
                    {selectedTarget && (
                      <div className="px-3 py-2 border-t bg-green-50 text-xs">
                        <span className="font-semibold">Selected:</span> {selectedTarget.lastName} ({selectedTarget.members.length})
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 border-t flex justify-end gap-2">
                <button
                  onClick={() => {
                    setMergeOpen(false);
                    setMergeSourceId('');
                    setMergeTargetId('');
                    setMergeSearchSource('');
                    setMergeSearchTarget('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded text-sm text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleMergeSave}
                  disabled={mergeSaving || !mergeSourceId || !mergeTargetId || mergeSourceId === mergeTargetId}
                  className="px-4 py-2 border border-transparent rounded text-sm text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300"
                >
                  {mergeSaving ? 'Merging...' : 'Merge'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Payment history modal */}
      {historyFamily && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">
                Payment history — {historyFamily.lastName}
              </h3>
              <button onClick={() => setHistoryFamily(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-4">
              {historyLoading ? (
                <div className="flex justify-center py-6">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : historyPayments.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-6">No payment records yet.</p>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Year</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {historyPayments.map((p) => (
                      <tr key={p.payment_id}>
                        <td className="px-3 py-2 text-sm font-medium text-gray-900">{p.competition_year}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            p.status === 'paid' ? 'bg-green-100 text-green-800' :
                            p.status === 'school_paid' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {p.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-700">{p.paid_at || '-'}</td>
                        <td className="px-3 py-2 text-sm text-gray-700">{p.amount ?? '-'}</td>
                        <td className="px-3 py-2 text-sm text-gray-700">{p.notes || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="px-6 py-4 border-t flex justify-end">
              <button
                onClick={() => setHistoryFamily(null)}
                className="px-4 py-2 border border-gray-300 rounded text-sm text-gray-700 bg-white hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FamilyConnectionsPage;